'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var actions = require('./actions.js');
var clientSideListStrategy = require('./clientSideListStrategy.js');
var ListContext = require('./ListContext.js');
var listStrategy = require('./listStrategy.js');
var reducers = require('./reducers.js');
var serverSideListStrategy = require('./serverSideListStrategy.js');
var sortTypes = require('./sortTypes.js');

var ListManager = function (_a) {
    var _b = _a.strategy, strategy = _b === void 0 ? listStrategy.ListStrategyType.CLIENT_SIDE : _b, _c = _a.columnOrder, columnOrder = _c === void 0 ? [] : _c, _d = _a.filter, initialFilter = _d === void 0 ? null : _d, _e = _a.list, list = _e === void 0 ? [] : _e, _f = _a.listSize, listSize = _f === void 0 ? list.length : _f, _g = _a.itemsPerPage, initialItemsPerPage = _g === void 0 ? list.length : _g, setList = _a.setList, _h = _a.sortBy, initialSortBy = _h === void 0 ? null : _h, _j = _a.sortDirection, initialSortDirection = _j === void 0 ? 1 : _j, _k = _a.sortType, initialSortType = _k === void 0 ? sortTypes.SortTypeTypes.ALPHANUMERIC : _k, children = _a.children;
    var listStrategy$1 = strategy === listStrategy.ListStrategyType.CLIENT_SIDE
        ? clientSideListStrategy.default
        : serverSideListStrategy.default;
    var initialState = {
        itemsPerPage: initialItemsPerPage,
        currentPage: 1,
        filter: initialFilter,
        sortBy: initialSortBy,
        sortDirection: initialSortDirection,
        sortType: initialSortType
    };
    var _l = react.useReducer(reducers.default, initialState), _m = _l[0], filter = _m.filter, sortBy = _m.sortBy, sortDirection = _m.sortDirection, sortType = _m.sortType, currentPage = _m.currentPage, _o = _m.itemsPerPage, itemsPerPage = _o === void 0 ? 1 : _o, dispatch = _l[1];
    var getColumns = react.useCallback(function () {
        var columns;
        if (columnOrder) {
            columns = columnOrder;
        }
        else {
            var flattened_1 = [];
            list.forEach(function (value) {
                Object.keys(value).map(function (k) { return flattened_1.push(k); });
            });
            var ListSet = new Set(flattened_1);
            columns = Array.from(ListSet);
        }
        return columns;
    }, [columnOrder, list]);
    var handleFilter = react.useCallback(function (entry) {
        var found = false;
        Object.values(entry).forEach(function (value) {
            if (!filter ||
                (!!value &&
                    value
                        .toString()
                        .toUpperCase()
                        .includes(filter.toString().toUpperCase()))) {
                found = true;
            }
        });
        return found;
    }, [filter]);
    var resolvedFilter = react.useCallback(function (entry) {
        if (!filter)
            return true;
        else {
            return typeof filter === 'function'
                ? filter(entry)
                : handleFilter(entry);
        }
    }, [filter, handleFilter]);
    var getNumPages = react.useCallback(function (newItemsPerPage) {
        // If this is a server-side list, then the number of pages
        // won't change even if a 'display filter' is applied. If it's
        // a client-side list, then apply the filter then count the records
        var currentSize = strategy === listStrategy.ListStrategyType.SERVER_SIDE
            ? listSize
            : list.filter(resolvedFilter).length;
        var iPerPage = newItemsPerPage !== null && newItemsPerPage !== void 0 ? newItemsPerPage : itemsPerPage;
        return Math.ceil(currentSize / iPerPage);
    }, [itemsPerPage, list, listSize, resolvedFilter, strategy]);
    var compareStrings = function (str1, str2) {
        return str1.toLowerCase().localeCompare(str2.toLowerCase());
    };
    var compareDates = function (d1, d2) {
        var parsed1 = d1 ? Date.parse(d1) : 0;
        var parsed2 = d2 ? Date.parse(d2) : 0;
        return parsed1 < parsed2 ? -1 : 1;
    };
    var handleSort = react.useCallback(function (a, b) {
        var _a, _b;
        var result;
        var val1 = "".concat((_a = (sortBy && a && a[sortBy])) !== null && _a !== void 0 ? _a : '');
        var val2 = "".concat((_b = (sortBy && b && b[sortBy])) !== null && _b !== void 0 ? _b : '');
        switch (sortType) {
            case sortTypes.SortTypeTypes.ALPHANUMERIC:
                result = compareStrings(val1, val2);
                break;
            case sortTypes.SortTypeTypes.CURRENCY:
                /**
                 *  null values they should go first whe sorting ascending by currency
                 */
                if (!val1) {
                    result = -1;
                }
                else if (!val2) {
                    result = 1;
                }
                else {
                    result =
                        parseFloat(val1.replace(/[^0-9a-z\.]/gi, '')) -
                            parseFloat(val2.replace(/[^0-9a-z\.]/gi, ''));
                }
                break;
            case sortTypes.SortTypeTypes.DATE:
                result = compareDates(val1, val2);
                break;
            case sortTypes.SortTypeTypes.NUMERIC:
                result = parseFloat(val1 || '0') - parseFloat(val2 || '0');
                break;
            default:
                result = compareStrings(val1, val2);
                break;
        }
        return result * sortDirection;
    }, [sortBy, sortDirection, sortType]);
    var getList = react.useCallback(function () {
        var pageIndexes = listStrategy$1.getIndexesForPage(itemsPerPage, currentPage);
        return strategy !== listStrategy.ListStrategyType.SERVER_SIDE
            ? list
                .filter(resolvedFilter)
                .sort(handleSort)
                .slice(pageIndexes.start, pageIndexes.stop)
            : list.filter(resolvedFilter).slice(pageIndexes.start, pageIndexes.stop);
    }, [
        listStrategy$1,
        itemsPerPage,
        currentPage,
        strategy,
        list,
        resolvedFilter,
        handleSort
    ]);
    var handlePageChange = react.useCallback(function (newPage) {
        dispatch(actions.setCurrentPage(+newPage));
    }, []);
    var handleItemsPerPageChange = react.useCallback(function (newIpp) {
        dispatch(actions.setItemsPerPage(newIpp || list.length));
        var numPages = getNumPages(newIpp);
        if (currentPage > numPages && numPages > 0) {
            handlePageChange(numPages);
        }
    }, [currentPage, getNumPages, handlePageChange, list.length]);
    var handleSortList = react.useCallback(function (newSortBy, newSortType, newSortDirection) { return dispatch(actions.sortList(newSortBy, newSortType, newSortDirection)); }, []);
    //*
    /* This useEffect takes care of setting currentPage to one
    /* each time we have a new list prop.
    /* Do not delete list variable from dependency array.
    */
    react.useEffect(function () {
        if (strategy === listStrategy.ListStrategyType.CLIENT_SIDE) {
            handlePageChange(1);
        }
    }, [handlePageChange, list, strategy]);
    var context = react.useMemo(function () { return ({
        getList: getList,
        getItemsPerPage: function () { return itemsPerPage; },
        getColumns: getColumns,
        sortList: handleSortList,
        filterList: function (newFilter) {
            return dispatch(actions.filterList(newFilter));
        },
        setItemsPerPage: handleItemsPerPageChange,
        getSortBy: function () { return sortBy; },
        getSortDirection: function () { return sortDirection; },
        getCurrentPage: function () { return currentPage; },
        setCurrentPage: handlePageChange,
        getNumPages: getNumPages,
        setList: setList,
        listStrategy: strategy,
        listSize: listSize
    }); }, [
        currentPage,
        getColumns,
        getList,
        getNumPages,
        handleItemsPerPageChange,
        handlePageChange,
        handleSortList,
        itemsPerPage,
        setList,
        sortBy,
        sortDirection,
        strategy,
        listSize
    ]);
    return (jsxRuntime.jsx(ListContext.default.Provider, tslib.__assign({ value: context }, { children: children })));
};
var ListManager$1 = ListManager;

exports.default = ListManager$1;
//# sourceMappingURL=ListManager.js.map
